import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import * as msal from '@azure/msal-browser';
import { EventMessage, EventType } from '@azure/msal-browser';
import { DictionariesApiService } from '@core/services/dictionaries.service';
import { UnsubscribeService } from '@core/services/unsubscribe.service';
import { TranslateService } from '@ngx-translate/core';
import { BaseApiService } from '@shared/base';
import { Account, Country, US_COUNTRY_CODE } from '@shared/models';
import { Observable, ReplaySubject, zip } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { StorageService } from './storage/storage.service';
import { MemberFirmService } from '@core/services/member-firm.service';

@Injectable({
  providedIn: 'root'
})
export class AccountService extends BaseApiService implements OnDestroy {
  get account$() {
    return this.accountSubj$.asObservable();
  }

  get countryCode$() {
    return this.countryCodeSubj$.asObservable();
  }

  get availableCountries$() {
    return this.availableCountriesSubj$.asObservable();
  }

  public account = {} as Account;

  public isAuthenticated: boolean;

  private accountSubj$ = new ReplaySubject<Account>(1);
  private countryCodeSubj$ = new ReplaySubject<string>(1);
  private availableCountriesSubj$ = new ReplaySubject<Country[]>(1);
  private refreshTokenInterval = 0;
  private readonly TOKEN_NAME = 'idToken';
  private readonly env = (window as any).__env__ || {};

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private dictionariesService: DictionariesApiService,
    private storageService: StorageService,
    private unsubscribe$: UnsubscribeService,
    private memberFirmService: MemberFirmService
  ) {
    super();
  }

  public ngOnDestroy(): void {
    clearInterval(this.refreshTokenInterval);
  }

  public init() {
    this.updateToken();
    this.storageService.localStorageCopy = { ...localStorage };

    this.setTranslations();
    this.loadDelaneyWidgetDA();

    setTimeout(() => {
      this.loadUser();
      this.loadCountries();
      this.initWebchatWidgetDA();
      this.memberFirmService.init();
    }, 500);
  }

  public loadUser(customHeaders = {}): void {
    this.http
      .get<Account>(`${this.queryBaseUrl}/users/current`, { headers: customHeaders })
      .subscribe(account => {
        this.account = account;
        this.accountSubj$.next(new Account(account));
      });
  }

  public setCountry(countryCode: string): void {
    this.countryCodeSubj$.next(countryCode);
  }

  public isFirstLogin(account: Account): boolean {
    if (this.getLSNotFirstLoginFlag('not_first_login')) {
      return false;
    }

    this.setLSNotFirstLoginFlag('not_first_login');
    return true;
  }

  public setLoginDisplay() {
    this.isAuthenticated = this.msalService.instance.getAllAccounts().length > 0;
  }

  public logout(popup?: boolean) {
    if (popup) {
      this.msalService.logoutPopup({
        mainWindowRedirectUri: '/'
      });
    } else {
      this.msalService.logoutRedirect();
    }
  }

  public getToken(): string {
    return localStorage.getItem(this.TOKEN_NAME);
  }

  public startVideoViewShowed(): Observable<any> {
    return this.http
      .post(`${this.commandBaseUrl}/startVideoView`, { visitorEmail: this.account.emailAddress })
      .pipe(tap(() => this.loadUser()));
  }

  private loadCountries(): void {
    zip(this.accountSubj$.pipe(take(1)), this.dictionariesService.getCountries()).subscribe(([account, countries]) => {
      const priorCountry = account.defaultMemberFirmName === 'USI' ? 'IN' : account.countryCode;
      const code = countries.some(({ countryCode }) => countryCode === priorCountry) ? priorCountry : US_COUNTRY_CODE;

      this.setCountry(code);
      this.availableCountriesSubj$.next([...countries]);
    });
  }

  private setTranslations(): void {
    this.countryCodeSubj$.subscribe(countryCode => {
      this.translate.setDefaultLang(countryCode);
      this.translate.use(countryCode);
    });
  }

  private getLSNotFirstLoginFlag(flagName: 'not_first_login'): boolean {
    return Boolean(window.localStorage.getItem(flagName));
  }

  private setLSNotFirstLoginFlag(flagName: 'not_first_login') {
    window.localStorage.setItem(flagName, 'true');
  }

  private updateToken(): void {
    this.setLoginDisplay();

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((result: EventMessage) => {
        this.setToken(result.payload[this.TOKEN_NAME]);
        this.setLoginDisplay();
      });
  }

  private loadDelaneyWidgetDA(): void {
    const script = document.createElement('script');

    script.setAttribute('src', `${this.env.widgetDA_URL}/assets/widget/js/delaney-widget.js`);
    script.async = true;
    document.head.appendChild(script);
  }

  private initWebchatWidgetDA(): void {
    const webchatElement = document.getElementById('da-webchat-widget');

    const bootDelaneyInterval = setInterval(() => {
      if (typeof (window as any).bootDelaney === 'function') {
        (window as any).bootDelaney(
          callback => {
            this.acquireTokenWidgetDA(this.msalService.instance.getAllAccounts()[0])
              .then(accessToken => callback(accessToken))
              .catch(error => console.log('error', error));
          },
          webchatElement,
          {
            originName: `${this.env.widgetDA_URL}/api/`,
            appKey: this.env.widgetDA_slate_app_key
          }
        );

        clearInterval(bootDelaneyInterval);
      }
    }, 500);
  }

  private acquireTokenWidgetDA(account: msal.AccountInfo): Promise<string> {
    const silentRequest = {
      scopes: ['openid', 'profile', `${this.env.widgetDA_client_ID}/.default`],
      account
    };
    return this.msalService.instance
      .acquireTokenSilent(silentRequest)
      .catch(error => {
        localStorage.removeItem('feedBackIsCompleted');
        if (error instanceof msal.InteractionRequiredAuthError) {
          return this.msalService.instance.loginPopup(silentRequest);
        } else {
          throw error;
        }
      })
      .then(tokenResponse => tokenResponse.accessToken);
  }

  private setToken(token: string): void {
    localStorage.setItem(this.TOKEN_NAME, token);
  }
}
